import { isRichDoc, isEmptyRichDoc, type RichDoc } from './staticPageRichText';

// Pinned to common-contracts/static_page_blocks.json (see staticPageBlocks.test.ts).
export const STATIC_PAGE_BLOCKS = {
  version: 1,
  blockTypes: ['rich_text', 'hero_slider', 'notice', 'page_links', 'album_open', 'album_list'],
  maxBlocksPerPage: 30,
  richTextColumns: { min: 1, max: 6 },
  heroSlides: { min: 1, max: 10 },
  captionPositions: ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'],
  captionMaxLength: 2000,
  noticeStyles: ['info', 'success', 'warning', 'danger'],
  maxPageLinks: 20,
} as const satisfies {
  version: 1;
  blockTypes: readonly string[];
  maxBlocksPerPage: number;
  richTextColumns: { min: number; max: number };
  heroSlides: { min: number; max: number };
  captionPositions: readonly CaptionPosition[];
  captionMaxLength: number;
  noticeStyles: readonly NoticeStyle[];
  maxPageLinks: number;
};

export type CaptionPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
export type NoticeStyle = 'info' | 'success' | 'warning' | 'danger';

export interface RichTextBlock {
  type: 'rich_text';
  columns: (string | RichDoc)[];
}

export interface HeroSlide {
  asset_ref: string;
  caption_md?: string;   // legacy markdown caption; new content uses `caption`
  caption?: RichDoc;
  caption_position?: CaptionPosition;
}

export interface HeroSliderBlock {
  type: 'hero_slider';
  slides: HeroSlide[];
}

export interface NoticeBlock {
  type: 'notice';
  markdown: string;
  style: NoticeStyle;
}

export interface PageLink {
  slug: string;
  label?: string;
}

export interface PageLinksBlock {
  type: 'page_links';
  links: PageLink[];
}

export interface AlbumOpenBlock {
  type: 'album_open';
}

export interface AlbumListBlock {
  type: 'album_list';
}

export type StaticPageBlock =
  | RichTextBlock
  | HeroSliderBlock
  | NoticeBlock
  | PageLinksBlock
  | AlbumOpenBlock
  | AlbumListBlock;

export interface BlockDocument {
  version: number;
  blocks: StaticPageBlock[];
}

/**
 * Parses a static page `body` string as a block document envelope. Returns
 * `null` for anything that isn't a JSON object with `version === 1` and an
 * array `blocks` whose elements are objects with a string `type` — plain
 * markdown (legacy body), a JSON array, or a document with an unsupported
 * version all fall through to `null`. Deeper per-block-type validation is
 * the server's job; block renderers must stay defensive about their own
 * fields.
 */
export function parseBlockDocument(body: string | null | undefined): BlockDocument | null {
  if (!body) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

  const candidate = parsed as { version?: unknown; blocks?: unknown };
  if (candidate.version !== 1) return null;
  if (!Array.isArray(candidate.blocks)) return null;

  const blocksValid = candidate.blocks.every(
    (block) => typeof block === 'object' && block !== null && !Array.isArray(block) && typeof (block as { type?: unknown }).type === 'string'
  );
  if (!blocksValid) return null;

  return parsed as BlockDocument;
}

/** An empty version-1 block document, used as the starting point for a new page. */
export function emptyBlockDocument(): BlockDocument {
  return { version: 1, blocks: [] };
}

/**
 * Wraps a legacy markdown `body` into a single-column rich_text block
 * document, for migrating pre-block-document pages into the editor.
 */
export function wrapLegacyMarkdown(md: string): BlockDocument {
  if (!md) return emptyBlockDocument();
  return { version: 1, blocks: [{ type: 'rich_text', columns: [md] }] };
}

/**
 * Normalizes a per-locale `body` string on its way to the server: drops the
 * invalid-but-harmless states the editor legitimately produces mid-edit, so a
 * half-configured block never turns into a 422 on save.
 *
 * Dropped blocks (each one is a server-side validation error, and each is the
 * exact shape a freshly added block starts out as):
 * - `hero_slider` with no slides
 * - `page_links` with no links
 * - `notice` with an empty `markdown` (whitespace-only is kept — the server
 *   accepts it, and it may be deliberate)
 *
 * A document that ends up carrying nothing (no blocks left, or a single
 * rich_text whose columns are all empty) serializes back to `''` so an
 * untouched locale stays "empty" rather than gaining a JSON envelope.
 *
 * Anything that isn't a block document (legacy markdown, empty string) is
 * returned unchanged.
 */
export function normalizeBodyForSave(raw: string): string {
  const doc = parseBlockDocument(raw);
  if (!doc) return raw;

  const blocks = doc.blocks.filter((block) => {
    switch (block.type) {
      case 'hero_slider':
        return (block.slides ?? []).length > 0;
      case 'page_links':
        return (block.links ?? []).length > 0;
      case 'notice':
        return block.markdown !== '';
      default:
        return true;
    }
  });

  const emptyColumn = (column: string | RichDoc) =>
    column === '' || (isRichDoc(column) && isEmptyRichDoc(column));
  const onlyBlock = blocks.length === 1 ? blocks[0] : undefined;
  const isEmpty =
    blocks.length === 0 ||
    (onlyBlock?.type === 'rich_text' && (onlyBlock.columns ?? []).every(emptyColumn));
  if (isEmpty) return '';

  return JSON.stringify({ ...doc, blocks });
}

/**
 * Resolves a block's asset reference to a displayable image URL. Admin
 * preview refs (`f2m-asset://<uuid>`) are resolved client-side against the
 * `assetUrls` map (empty string when not present there); any other ref is
 * already a resolved URL from the server (the public page path) and is
 * returned as-is.
 */
export function assetSrc(ref: string, assetUrls?: Record<string, string>): string {
  const prefix = 'f2m-asset://';
  if (ref.startsWith(prefix)) {
    const id = ref.slice(prefix.length);
    return assetUrls?.[id] ?? '';
  }
  return ref;
}
